import { useMutation, useQuery } from '@tanstack/react-query';
import {
  calculateCommute,
  calculateSalary,
  fetchCareerPath,
  fetchPracticeHub,
  sendPracticeMessage,
  startPracticeSession,
  type CommuteCalculatorPayload,
  type CommuteCalculatorResult,
  type PracticeChatResponse,
  type PracticeHubResponse,
  type PracticeSessionStartPayload,
  type PracticeSessionStartResponse,
  type SalaryCalculatorPayload,
  type SalaryCalculatorResult,
} from '../api/practiceHub';

export function usePracticeHub() {
  return useQuery<PracticeHubResponse>({
    queryKey: ['practice-hub'],
    queryFn: fetchPracticeHub,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCareerPath() {
  return useQuery({
    queryKey: ['career-path'],
    queryFn: fetchCareerPath,
    staleTime: 1000 * 60 * 10,
  });
}

export function usePracticeSession() {
  const startMutation = useMutation<PracticeSessionStartResponse, Error, PracticeSessionStartPayload>({
    mutationFn: startPracticeSession,
  });

  const chatMutation = useMutation<
    PracticeChatResponse,
    Error,
    { sessionId: string; message: string }
  >({
    mutationFn: ({ sessionId, message }) => sendPracticeMessage(sessionId, message),
  });

  return {
    startSession: startMutation.mutateAsync,
    isStarting: startMutation.isPending,
    sendMessage: chatMutation.mutateAsync,
    isSending: chatMutation.isPending,
  };
}

export function useCommuteCalculator() {
  return useMutation<CommuteCalculatorResult, Error, CommuteCalculatorPayload>({
    mutationFn: calculateCommute,
  });
}

export function useSalaryCalculator() {
  return useMutation<SalaryCalculatorResult, Error, SalaryCalculatorPayload>({
    mutationFn: calculateSalary,
  });
}
